import { InputAdornment, useTheme } from "@mui/material";
import TextField from "@mui/material/TextField";
import { forwardRef } from "react";
import { deriveInputTestId, withInputTestId } from "helpers/testId";

/**
 * DOM contract: `data-testid` is forwarded to the underlying `<input>` rather
 * than the `.MuiFormControl-root` wrapper, so `getByTestId(id).fill(...)` acts
 * on the control directly. See `helpers/testId`.
 *
 * When no `data-testid` is given, the id is derived from the form field
 * `name` - `inputProps` is normally a `register("clientName")` result - so a
 * field is addressable as `client-name` without the call site supplying an id.
 * Pass an explicit id where two fields on one screen share a name.
 */
const StandardTextField = forwardRef((props: any, ref) => {
  const {
    startIcon,
    size,
    margin,
    endIcon,
    inputProps,
    "data-testid": explicitTestId,
    ...other
  } = props;
  const testId = deriveInputTestId(explicitTestId, inputProps, other);
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === "dark";

  return (
    <TextField
      margin={margin}
      size={size}
      ref={ref}
      variant="standard"
      {...other}
      slotProps={{
        input: {
          startAdornment: startIcon ? (
            <InputAdornment
              sx={{ color: isDarkTheme ? "" : "#08233F" }}
              position="start"
            >
              {startIcon}
            </InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment
              sx={{ color: isDarkTheme ? "" : "#08233F" }}
              position="end"
            >
              {endIcon}
            </InputAdornment>
          ) : undefined,
        },
        htmlInput: withInputTestId(testId, inputProps),
      }}
    />
  );
});

export default StandardTextField;
